/**
 * URL-synced global state: pure codec. A URL is a view — everything here
 * round-trips through the search params (see url.test.ts). The React hook
 * `useUrlState` wraps this with react-router's search params.
 *
 * Search keys:
 *   r=24h            preset (pinned to now)                 default 24h
 *   from=&to=        explicit unix seconds (not pinned)
 *   pin=1            with `from` only: to = now, follows live
 *   b=300            bucket seconds; absent = auto
 *   kind=buy,sell  purpose=  tier=  flagged=1|0  role=  tag=  source=  family=  venue=  quote=  priced=1|0  min_usd=
 *   sel=0xa,0xb      selected families
 *   live=0           live mode off (default on)
 *   cur=quote        price currency (default usd)
 */
import type { Currency, Kind, PurposeFilter, Source, Tier } from "@/api/types";
import { PURPOSE_FILTERS } from "@/lib/purpose";
import { isBucket } from "@/lib/bucket";
import { isPreset, nowSecs, PRESET_SECS, type Preset } from "@/lib/time";

export interface TimeRange {
  preset?: Preset;
  from?: number;
  to?: number;
  /** `to` follows "now": presets are pinned, explicit ranges are not unless pin=1. */
  pinned: boolean;
}

export interface Filters {
  kind: Kind[];
  purpose: PurposeFilter[];
  tier: Tier[];
  flagged?: boolean;
  role: string[];
  tag: string[];
  source: Source[];
  family: string[];
  venue: string[];
  /** quote-currency symbols (contract §2.5 `quote`), e.g. ["USDC"] */
  quote: string[];
  priced?: boolean;
  min_usd?: number;
}

export interface UrlState {
  range: TimeRange;
  bucket?: number;
  filters: Filters;
  selection: string[];
  live: boolean;
  currency: Currency;
}

export const DEFAULT_PRESET: Preset = "24h";

export const EMPTY_FILTERS: Filters = {
  kind: [],
  purpose: [],
  tier: [],
  role: [],
  tag: [],
  source: [],
  family: [],
  venue: [],
  quote: [],
};

export const DEFAULT_STATE: UrlState = {
  range: { preset: DEFAULT_PRESET, pinned: true },
  filters: EMPTY_FILTERS,
  selection: [],
  live: true,
  currency: "usd",
};

const KINDS: readonly Kind[] = [
  "buy",
  "sell",
  "send",
  "receive",
  "mint",
  "burn",
  "add_liquidity",
  "remove_liquidity",
  "collect_fees",
];
const TIERS: readonly Tier[] = ["none", "small", "big", "whale"];
const SOURCES: readonly Source[] = ["distributor", "market", "liquidity"];

export const FILTER_KEYS = [
  "kind",
  "purpose",
  "tier",
  "flagged",
  "role",
  "tag",
  "source",
  "family",
  "venue",
  "quote",
  "priced",
  "min_usd",
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];
export type ListFilterKey =
  "kind" | "purpose" | "tier" | "role" | "tag" | "source" | "family" | "venue" | "quote";

function list(p: URLSearchParams, key: string): string[] {
  const out: string[] = [];
  for (const raw of p.getAll(key)) {
    for (const v of raw.split(",")) {
      const s = v.trim();
      if (s && !out.includes(s)) out.push(s);
    }
  }
  return out;
}

function enumList<T extends string>(p: URLSearchParams, key: string, allowed: readonly T[]): T[] {
  return list(p, key).filter((v): v is T => (allowed as readonly string[]).includes(v));
}

function num(p: URLSearchParams, key: string): number | undefined {
  const raw = p.get(key);
  if (raw === null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function bool(p: URLSearchParams, key: string): boolean | undefined {
  const raw = p.get(key);
  if (raw === null) return undefined;
  if (raw === "1" || raw === "true") return true;
  if (raw === "0" || raw === "false") return false;
  return undefined;
}

export function parseUrlState(params: URLSearchParams): UrlState {
  const from = num(params, "from");
  const to = num(params, "to");
  const presetRaw = params.get("r");
  const preset = presetRaw && isPreset(presetRaw) ? presetRaw : undefined;
  let range: TimeRange;
  if (from !== undefined || to !== undefined) {
    const pinned = to === undefined ? true : bool(params, "pin") === true;
    range = { from, to: pinned ? undefined : to, pinned };
  } else {
    range = { preset: preset ?? DEFAULT_PRESET, pinned: true };
  }
  const bucketRaw = num(params, "b");
  const bucket = bucketRaw !== undefined && isBucket(bucketRaw) ? bucketRaw : undefined;
  const filters: Filters = {
    kind: enumList(params, "kind", KINDS),
    purpose: enumList(params, "purpose", PURPOSE_FILTERS),
    tier: enumList(params, "tier", TIERS),
    flagged: bool(params, "flagged"),
    role: list(params, "role"),
    tag: list(params, "tag"),
    source: enumList(params, "source", SOURCES),
    family: list(params, "family").map((a) => a.toLowerCase()),
    venue: list(params, "venue").map((a) => a.toLowerCase()),
    quote: list(params, "quote"),
    priced: bool(params, "priced"),
    min_usd: num(params, "min_usd"),
  };
  const cur = params.get("cur");
  return {
    range,
    bucket,
    filters,
    selection: list(params, "sel").map((a) => a.toLowerCase()),
    live: bool(params, "live") ?? true,
    currency: cur === "quote" ? "quote" : "usd",
  };
}

/** Serialize into `into` (mutated), leaving unrelated keys untouched. Defaults are omitted. */
export function writeUrlState(
  state: UrlState,
  into: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const p = into;
  const del = (...keys: string[]) => keys.forEach((k) => p.delete(k));
  const setList = (key: string, values: string[]) =>
    values.length ? p.set(key, values.join(",")) : p.delete(key);
  const setBool = (key: string, v: boolean | undefined) =>
    v === undefined ? p.delete(key) : p.set(key, v ? "1" : "0");
  const setNum = (key: string, v: number | undefined) =>
    v === undefined ? p.delete(key) : p.set(key, String(v));

  del("r", "from", "to", "pin");
  const r = state.range;
  if (r.from !== undefined || r.to !== undefined) {
    setNum("from", r.from);
    if (r.pinned) {
      if (r.from !== undefined) p.set("pin", "1");
    } else {
      setNum("to", r.to);
    }
  } else if (r.preset && r.preset !== DEFAULT_PRESET) {
    p.set("r", r.preset);
  }
  setNum("b", state.bucket);
  const f = state.filters;
  setList("kind", f.kind);
  setList("purpose", f.purpose);
  setList("tier", f.tier);
  setBool("flagged", f.flagged);
  setList("role", f.role);
  setList("tag", f.tag);
  setList("source", f.source);
  setList("family", f.family);
  setList("venue", f.venue);
  setList("quote", f.quote);
  setBool("priced", f.priced);
  setNum("min_usd", f.min_usd);
  setList("sel", state.selection);
  if (state.live) p.delete("live");
  else p.set("live", "0");
  if (state.currency === "quote") p.set("cur", "quote");
  else p.delete("cur");
  return p;
}

export interface ResolvedRange {
  from: number;
  to: number;
  pinned: boolean;
  preset?: Preset;
}

/**
 * Wall-clock grid a live ("now") range is resolved on. `resolveRange` puts
 * `now` straight into `to`, and every slice puts `to` into its react-query
 * keys; without a grid any re-render landing in a new second mints a new key
 * for every pane on the page — a second HTTP request and a second cache entry
 * held for `gcTime`. 30 s is far below the SSE change rate that actually
 * brings new rows in, so freshness comes from invalidation, not key churn.
 */
export const LIVE_TICK_SECS = 30;

/** `nowSecs()` floored to the [`LIVE_TICK_SECS`] grid. */
export function liveTick(now = nowSecs()): number {
  return Math.floor(now / LIVE_TICK_SECS) * LIVE_TICK_SECS;
}

/** Concrete [from, to) for API calls. `all` -> from 0. */
export function resolveRange(range: TimeRange, now = nowSecs()): ResolvedRange {
  if (range.from !== undefined || range.to !== undefined) {
    const to = range.pinned || range.to === undefined ? now : range.to;
    const from = range.from ?? Math.max(0, to - PRESET_SECS["24h"]);
    return { from: Math.min(from, to), to, pinned: range.pinned };
  }
  const preset = range.preset ?? DEFAULT_PRESET;
  if (preset === "all") return { from: 0, to: now, pinned: true, preset };
  return { from: now - PRESET_SECS[preset], to: now, pinned: true, preset };
}

/** Shift a resolved range by a fraction of its span (negative = earlier). Unpins. */
export function nudgeRange(range: TimeRange, fraction: number, now = nowSecs()): TimeRange {
  const r = resolveRange(range, now);
  const span = r.to - r.from;
  const delta = Math.round(span * fraction);
  const to = Math.min(now, r.to + delta);
  return { from: Math.max(0, to - span), to, pinned: false };
}

/** Zoom around the range end (factor < 1 = zoom in). Keeps the pin. */
export function zoomRange(range: TimeRange, factor: number, now = nowSecs()): TimeRange {
  const r = resolveRange(range, now);
  const span = Math.max(60, Math.round((r.to - r.from) * factor));
  if (r.pinned) return { from: Math.max(0, now - span), pinned: true };
  const mid = (r.from + r.to) / 2;
  return { from: Math.max(0, Math.round(mid - span / 2)), to: Math.round(mid + span / 2), pinned: false };
}

export function hasActiveFilters(f: Filters): boolean {
  return (
    f.kind.length +
      f.purpose.length +
      f.tier.length +
      f.role.length +
      f.tag.length +
      f.source.length +
      f.family.length +
      f.venue.length +
      f.quote.length >
      0 ||
    f.flagged !== undefined ||
    f.priced !== undefined ||
    f.min_usd !== undefined
  );
}

/** Filters as query params for the API (contract §2.5 names). */
export function filtersToQuery(f: Filters): Record<string, string | number | boolean | string[] | undefined> {
  return {
    kind: f.kind.length ? f.kind : undefined,
    purpose: f.purpose.length ? f.purpose : undefined,
    tier: f.tier.length ? f.tier : undefined,
    flagged: f.flagged,
    role: f.role.length ? f.role : undefined,
    tag: f.tag.length ? f.tag : undefined,
    source: f.source.length ? f.source : undefined,
    family: f.family.length ? f.family : undefined,
    venue: f.venue.length ? f.venue : undefined,
    quote: f.quote.length ? f.quote : undefined,
    priced: f.priced,
    min_usd: f.min_usd,
  };
}
